import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useSidebar } from '../../contexts/SidebarContext';
import Header from '../../components/ui/Header';
import EnhancedSidebar from '../../components/ui/EnhancedSidebar';
import EnhancedBreadcrumb from '../../components/ui/EnhancedBreadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CampaignBuilder from '../settings/components/CampaignBuilder';
import campaignService from '../../utils/campaignService';
import ErrorBoundary from '../../components/ErrorBoundary';

const Campaigns = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { currentOrganization } = useOrganization();
  const { sidebarExpanded, handleSidebarToggle } = useSidebar();

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Message handler for components
  const handleMessage = (type, message) => {
    if (type === 'success') {
      setSuccessMessage(message);
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } else if (type === 'error') {
      setErrorMessage(message);
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  // Load campaign data from database
  useEffect(() => {
    const loadCampaign = async () => {
      if (!currentOrganization?.id) return;

      try {
        setLoading(true);
        setErrorMessage('');

        // Try to get existing campaign
        let campaignData = await campaignService.getCampaign(currentOrganization.id);
        
        // If no campaign exists, use default
        if (!campaignData) {
          campaignData = campaignService.getDefaultCampaign();
          // Add organization-specific data
          campaignData.id = currentOrganization.id;
        }

        // Add mock stats for now (TODO: implement stats tracking)
        campaignData.stats = {
          sent: 0,
          responses: 0,
          positive: 0
        };

        setCampaign(campaignData);
      } catch (err) {
        console.error('Error loading campaign:', err);
        handleMessage('error', 'Failed to load campaign data');
        
        // Fall back to default campaign
        const defaultCampaign = campaignService.getDefaultCampaign();
        defaultCampaign.id = currentOrganization.id;
        defaultCampaign.stats = { sent: 0, responses: 0, positive: 0 };
        setCampaign(defaultCampaign);
      } finally {
        setLoading(false);
      }
    };

    loadCampaign();
  }, [currentOrganization?.id]);

  const handleCampaignUpdate = (field, value) => {
    setCampaign(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveCampaign = async () => {
    if (!currentOrganization?.id || !campaign) return;

    try {
      setLoading(true);
      
      // Save the entire campaign
      const updatedCampaign = await campaignService.upsertCampaign(
        currentOrganization.id, 
        campaign
      );
      
      // Update local state with saved data, preserving frontend structure
      setCampaign(prev => {
        const updated = { ...prev, ...updatedCampaign };
        
        // Preserve the full questions structure with enabled field
        // Don't overwrite with database response that only has filtered questions
        if (campaign.questions && campaign.questions.length > 0 && campaign.questions[0].hasOwnProperty('enabled')) {
          updated.questions = campaign.questions; // Keep the full frontend structure
        }
        
        return updated;
      });
      
      handleMessage('success', 'Campaign saved successfully!');
    } catch (err) {
      console.error('Error saving campaign:', err);
      handleMessage('error', 'Failed to save campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step-specific save handler for individual steps
  const handleSaveStep = async (step, stepData) => {
    if (!currentOrganization?.id) return;

    try {
      // Ensure campaign exists first
      if (!campaign?.id) {
        // Create campaign with current data first
        await handleSaveCampaign();
        return;
      }

      // Save just this step's data
      const updatedCampaign = await campaignService.updateCampaignStep(
        currentOrganization.id, 
        step, 
        stepData
      );
      
      // Update local state, but preserve frontend-specific fields for Step 1
      setCampaign(prev => {
        const updated = { ...prev, ...updatedCampaign };
        
        // For Step 1, preserve the full questions structure with enabled field
        // Don't overwrite with filtered database response
        if (step === 1 && stepData.questions) {
          updated.questions = stepData.questions; // Keep the full frontend structure
        }
        
        return updated;
      });
      
      return updatedCampaign;
    } catch (err) {
      console.error(`Error saving step ${step}:`, err);
      throw err; // Re-throw so individual components can handle it
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading campaign...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Campaign - ServiceFlow Pro</title>
        <meta name="description" content="Configure your review request campaign" />
      </Helmet>

      <Header onSidebarToggle={handleSidebarToggle} sidebarExpanded={sidebarExpanded} />
      <EnhancedSidebar expanded={sidebarExpanded} onToggle={handleSidebarToggle} />

      <main className={`pt-16 transition-smooth ${sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'}`}>
        <EnhancedBreadcrumb />

        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground">Campaign Configuration</h1>
            <p className="text-muted-foreground mt-1">
              Customize your review request experience for all customers.
            </p>
          </div>

          {/* Campaign Builder */}
          {loading ? (
            <div className="bg-card rounded-lg border border-border min-h-[600px] flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <h3 className="text-lg font-medium text-foreground mb-2">Loading Campaign</h3>
                <p className="text-muted-foreground">Setting up your review flow configuration...</p>
              </div>
            </div>
          ) : campaign ? (
            <ErrorBoundary fallbackMessage="There was an error loading the Campaign Builder. Please try refreshing the page.">
              <CampaignBuilder 
                campaignData={campaign}
                organizationData={currentOrganization}
                onDataChange={handleCampaignUpdate}
                onMessage={handleMessage}
                onSave={handleSaveCampaign}
                onSaveStep={handleSaveStep}
              />
            </ErrorBoundary>
          ) : (
            <div className="bg-card rounded-lg border border-border min-h-[600px] flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertCircle" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No Campaign Data</h3>
                <p className="text-muted-foreground mb-4">Unable to load campaign configuration.</p>
                <Button onClick={() => window.location.reload()} variant="primary">
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Retry
                </Button>
              </div>
            </div>
          )}

          {/* Messages */}
          {successMessage && (
            <div className="fixed bottom-4 right-4 p-4 bg-chart-1/10 border border-chart-1/20 rounded-lg shadow-lg z-50">
              <div className="flex items-center">
                <Icon name="CheckCircle" size={16} className="text-chart-1 mr-2" />
                <span className="text-chart-1 text-sm">{successMessage}</span>
              </div>
            </div>
          )}

          {errorMessage && (
            <div className="fixed bottom-4 right-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg shadow-lg z-50">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={16} className="text-destructive mr-2" />
                <span className="text-destructive text-sm">{errorMessage}</span>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Campaigns; 